// MultiStackLine (insight_stackbar_linechart): stacked bars + a line overlay.
// Drawn natively with the already-loaded Plotly — no vendored class exists for
// this type. Bar measures stack on the left axis; the last measure (when there
// are 2+) renders as a line on a right axis.

import { useEffect, useRef, useState } from 'react'
import { ensurePlotly } from './loader'
import { mapVisualData, dimensions, measures, toNumber, formatValue, pickGroup } from './dataUtils'
import type { NativeRendererProps } from './nativeRenderers'
import { applyEventOverlay, mountEventStrip } from './events/overlay'
import type { EventsBlock } from './events/model'

export function ComboChart({ jsonData, visualLayout, eventHooks }: NativeRendererProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const md = mapVisualData(jsonData, visualLayout)
    const dims = dimensions(md)
    const ms = measures(md)
    if (!dims.length || !ms.length) {
      setErr('Needs a dimension and at least one measure.')
      return
    }
    const x = md.rows.map((r) => formatValue(r[dims[0].name], dims[0].type))
    const hidden: Record<string, boolean> = {}

    ensurePlotly().then(() => {
      if (cancelled || !ref.current) return
      const P = (window as unknown as { Plotly?: { newPlot: (...a: unknown[]) => void; purge: (e: Element) => void } }).Plotly
      if (!P) { setErr('Plotly failed to load.'); return }
      const barMs = ms.length > 1 ? ms.slice(0, -1) : ms
      const lineM = ms.length > 1 ? ms[ms.length - 1] : null
      const traces: Record<string, unknown>[] = barMs.map((m) => ({
        type: 'bar', name: m.label, x, y: md.rows.map((r) => toNumber(r[m.name])),
      }))
      if (lineM) {
        traces.push({
          type: 'scatter', mode: 'lines+markers', name: lineM.label, x,
          y: md.rows.map((r) => toNumber(r[lineM.name])), yaxis: 'y2',
        })
      }
      const layout: Record<string, unknown> = {
        barmode: 'stack', margin: { t: 20, r: 50, b: 70, l: 55 }, showlegend: true,
        xaxis: { type: 'category' },
        yaxis2: { overlaying: 'y', side: 'right', showgrid: false },
        paper_bgcolor: 'transparent', plot_bgcolor: 'transparent',
      }
      // Events on the timeline — the same overlay the vendored line charts use.
      const events = (visualLayout as { events?: EventsBlock }).events
      let overlay = null
      if (events?.layers?.length) {
        try {
          const evField = pickGroup(md, 'events')[0]?.name ?? null
          overlay = applyEventOverlay({
            traces, layout, events, rows: md.rows, rowField: dims[0].name, eventsField: evField,
            rowLabels: md.rows.map((r, i) => [r[dims[0].name], x[i]] as [unknown, string]), hidden,
          })
          if (overlay) layout.margin = { t: 40, r: 50, b: 70, l: 55 }
        } catch (e) {
          console.warn('[ComboChart] events overlay skipped', e)
        }
      }
      P.newPlot(ref.current, traces, layout, { displayModeBar: false, responsive: true })
      if (overlay && ref.current) {
        try { mountEventStrip(ref.current, ref.current, overlay, hidden, eventHooks ?? {}) } catch (e) {
          console.warn('[ComboChart] events strip skipped', e)
        }
      }
    }).catch((e) => { if (!cancelled) setErr(String(e)) })

    return () => {
      cancelled = true
      const P = (window as unknown as { Plotly?: { purge: (e: Element) => void } }).Plotly
      if (ref.current && P) P.purge(ref.current)
    }
  }, [jsonData, visualLayout, eventHooks])

  if (err) return <div style={{ padding: 16, fontSize: 13, color: 'var(--pg-error, var(--color-error, #c33))', textAlign: 'center' }}>{err}</div>
  return <div ref={ref} style={{ width: '100%', height: '100%', minHeight: 240 }} />
}

export default ComboChart
